import { createContext, ReactNode } from "react";

// These are intentionally made explicit and static to ensure that the
// identifier is always the same for a given data and type.
// Note that we could instead parse the scope to use as dataKey.
const dataKey = new TextEncoder().encode("1234567890123456");
const iv = new TextEncoder().encode("1234567890123456");

const encrypt = async ({ data }: { data: string }) => {
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(data);

  const cryptoKey = await crypto?.subtle?.importKey?.(
    "raw",
    dataKey,
    { name: "AES-CBC" },
    false,
    ["encrypt"],
  );

  const encrypted = await crypto?.subtle?.encrypt?.(
    { name: "AES-CBC", iv },
    cryptoKey,
    encodedData,
  );

  return btoa(String.fromCharCode(...new Uint8Array(encrypted)))
    .substring(0, 20)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export const getTrackingIdHelper = async (
  data: string,
  component: string,
  scope: string,
) => {
  const encryption = await encrypt({ data });
  return `_${scope}_${component}_${encryption ?? ""}`;
};

export interface TrackingProviderContextProps {
  scope: string;
  getTrackingId: (data: string, component: string) => Promise<string>;
  optOut: boolean;
}

export interface TrackingProviderProps {
  children: ReactNode;
  scope: string;
  optOut?: boolean;
}

const defaultContext: TrackingProviderContextProps = {
  scope: "ST",
  getTrackingId: (data: string, component: string) =>
    getTrackingIdHelper(data, component, "ST"),
  optOut: false,
};

export const TrackingContext = createContext(defaultContext);
